/**
 * chip_fill_controller.js — Chip AI fills a form from what the user types or says (Stimulus).
 *
 * Sits on the <form>. The Chip button at the top of the form opens a bar with
 * a text box and a mic; the user types and presses Fill (or Enter), or taps the
 * mic and speaks, and the fill starts by itself once the transcript is in the
 * box. The form fills in front of them, one field at a time as the server
 * streams values back (POST /api/ai/fill/stream, application/api/ai_form_fill.py).
 * Nothing is submitted: the user checks the form and submits it as usual.
 *
 * Which fields Chip may fill is read off the form itself, so no form is
 * described twice. A field opts in with `data-chip-fill`, whose value is an
 * optional hint for the model ("only if a single total is stated"). The field's
 * name is its `name`, or `data-chip-fill-name` for an element without one (the
 * notes editor, the line-items section). Its kind follows from the element:
 *   <select>, or radios sharing a name      -> choice (its options are sent)
 *   <input type="number">                   -> number
 *   the create-line-items section           -> items (rows, via fillItem())
 *   any other input, textarea, contenteditable -> text
 * The label is `data-chip-fill-label`, else the <label for>, else the
 * placeholder.
 *
 * Every write dispatches the events a user's own edit would (input/change;
 * text and radios through _field_write.js, shared with the walkthrough), so
 * the controllers that watch those fields (client-mode, client-type,
 * site-picker, contact-picker, form-guard, the notes editor) react exactly as
 * they do to a person. Each write also records how to take itself back; Undo
 * replays those in reverse.
 *
 * Address fields (an input carrying an AddressAutocomplete, address_autocomplete.js)
 * are written without searching, then resolved one at a time once the fill is
 * done, so two suggestion lists never open at once. Chip picks a suggestion
 * itself only when the words match exactly one (_address_match.js); otherwise
 * that field's list opens for the user, and the next address waits until they
 * pick or close it. A picked address still reopens its list when tapped.
 *
 * Markup: templates/macros/chip_fill.html. Styles: static/css/mobile-forms.css.
 */
import { Controller } from "@hotwired/stimulus";
import { confidentMatch } from "./_address_match.js";
import { check, uncheck, writeText } from "./_field_write.js";
import { readSse } from "./_sse.js";

const FILL_URL = "/api/ai/fill/stream";
const FIELD = "[data-chip-fill]";
const FLASH_MS = 1500;
const MSG_FAILED = "Chip couldn't fill the form. Please try again.";

export default class extends Controller {
    static targets = ["bar", "input", "go", "status", "undo"];

    connect() {
        this._reverts = [];
        this._addresses = [];
        this._runs = 0;
    }

    disconnect() {
        this._abort?.abort();
        this._runs++;
    }

    toggle() {
        this.barTarget.hidden = !this.barTarget.hidden;
        if (!this.barTarget.hidden) this.inputTarget.focus();
    }

    // Enter fills; Shift+Enter is a new line.
    keydown(event) {
        if (event.key !== "Enter" || event.shiftKey) return;
        event.preventDefault();
        this.fill();
    }

    // Each fill is a run; a later fill, an undo or leaving the page ends the
    // run, so an address list the user left open cannot drive an older fill.
    async fill() {
        const text = this.inputTarget.value.trim();
        if (!text || this._abort) return;
        const run = ++this._runs;
        this._abort = new AbortController();
        this._reverts = [];
        this._addresses = [];
        this._setBusy(true);
        let done = false;
        try {
            done = await this._stream(text);
        } catch (error) {
            if (error.name !== "AbortError") this._say(MSG_FAILED);
        } finally {
            this._abort = null;
            this._setBusy(false);
        }
        if (done) await this._resolveAddresses(run);
    }

    undo() {
        this._runs++;
        this._reverts.reverse().forEach((revert) => revert());
        this._reverts = [];
        this.undoTarget.hidden = true;
        this._say("");
    }

    // --- The request -----------------------------------------------------

    // True when the server finished the fill (its `done` event arrived).
    async _stream(text) {
        const response = await fetch(FILL_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json", "Accept": "text/event-stream" },
            body: JSON.stringify({ text, fields: this._fields() }),
            credentials: "same-origin",
            signal: this._abort.signal,
        });
        if (response.status === 402) {
            this._showUpgrade();
            return false;
        }
        if (!response.ok || !response.body) throw new Error("fill request failed");
        this._say("Filling…");
        this._done = false;
        await readSse(response, (event, data) => this._onEvent(event, data));
        return this._done;
    }

    _onEvent(event, data) {
        if (event === "field") this._record(this._applyField(data.name, data.value));
        else if (event === "item") this._record(this._applyItem(data.name, data.value));
        else if (event === "done") this._finish(data.filled);
        else if (event === "error") this._say(data.message || MSG_FAILED);
    }

    // A fill that changed the form empties the box, so a follow-up starts
    // fresh: each fill sends only what is in the box, never earlier requests.
    // When nothing was filled the words stay, to be added to and tried again.
    _finish(filled) {
        this._done = true;
        this._summary = filled ? `Filled ${filled} ${filled === 1 ? "field" : "fields"}. Check it over, then save.`
                               : "Chip didn't find anything to fill in. Try saying more.";
        this._say(this._summary);
        this.undoTarget.hidden = this._reverts.length === 0;
        if (filled) this.inputTarget.value = "";
    }

    _record(revert) {
        if (revert) this._reverts.push(revert);
    }

    // --- The form's fields, as the server is told about them -------------

    _fields() {
        const fields = new Map();
        this._elements().forEach((el) => {
            const name = fieldName(el);
            const known = fields.get(name);
            if (known && el.type === "radio") known.options.push(radioOption(el));
            else if (!known) fields.set(name, describe(el, name));
        });
        // A choice with nothing to choose yet (no clients) is not offered.
        return [...fields.values()].filter((field) => !field.options || field.options.length);
    }

    _elements(name = null) {
        const all = [...this.element.querySelectorAll(FIELD)];
        return name ? all.filter((el) => fieldName(el) === name) : all;
    }

    // --- Writing values --------------------------------------------------

    _applyField(name, value) {
        const elements = this._elements(name);
        const el = elements[0];
        if (!el) return null;
        reveal(el);
        const revert = el.type === "radio" ? chooseRadio(elements, value)
            : el.tagName === "SELECT" ? chooseOption(el, value)
            : el.autocompleteInstance ? this._writeAddress(el, value)
            : writeText(el, value);
        if (revert) flash(el.type === "radio" ? elements.find((radio) => radio.checked) : el);
        return revert;
    }

    _writeAddress(input, value) {
        const autocomplete = input.autocompleteInstance;
        const before = input.value;
        autocomplete.setText(String(value));
        this._addresses.push(input);
        return () => autocomplete.setText(before);
    }

    // --- Resolving addresses, one at a time ------------------------------

    async _resolveAddresses(run) {
        let picked = 0;
        for (const input of this._addresses) {
            if (run !== this._runs) return;
            if (input.isConnected && await this._resolveAddress(input)) picked++;
        }
        if (this._addresses.length && run === this._runs) {
            this._say(picked ? `${this._summary} Tap an address to change it.` : this._summary);
        }
    }

    // True when Chip picked the suggestion itself.
    async _resolveAddress(input) {
        const autocomplete = input.autocompleteInstance;
        const query = input.value.trim();
        const suggestions = await autocomplete.suggest(query);
        const match = confidentMatch(query, suggestions);
        if (match) {
            this._record(pickAddress(input, match));
            return true;
        }
        if (suggestions.length) {
            input.scrollIntoView({ block: "center", behavior: "smooth" });
            this._say(`Which is the right ${labelOf(input, input.name).toLowerCase()}?`);
            await autocomplete.offer(suggestions);
        }
        return false;
    }

    _applyItem(name, item) {
        const [el] = this._elements(name);
        const lineItems = el && this.application.getControllerForElementAndIdentifier(el, "create-line-items");
        return lineItems ? lineItems.fillItem(item) : null;
    }

    // --- The bar ---------------------------------------------------------

    _setBusy(busy) {
        this.goTarget.disabled = busy;
        this.inputTarget.readOnly = busy;
        this.element.classList.toggle("chip-filling", busy);
        if (busy) {
            this.undoTarget.hidden = true;
            this._say("Reading…");
        }
    }

    _say(message) {
        this.statusTarget.textContent = message;
        this.statusTarget.hidden = !message;
    }

    // A 402 is a business without an active plan. The native shells get the
    // reason without the plans link (App Store 3.1.1 / Play Payments).
    _showUpgrade() {
        this._say("Chip AI form fill is included with an active subscription.");
        if (document.documentElement.hasAttribute("data-hotwire-native")) return;
        const link = document.createElement("a");
        link.href = "/billing/plans";
        link.className = "ms-1";
        link.textContent = "View plans";
        this.statusTarget.append(link);
    }
}

// --- Reading a field ------------------------------------------------------

function fieldName(el) {
    return el.dataset.chipFillName || el.name;
}

function describe(el, name) {
    const field = { name, label: labelOf(el, name), kind: kindOf(el), hint: el.dataset.chipFill || "" };
    if (el.type === "radio") field.options = [radioOption(el)];
    else if (el.tagName === "SELECT") field.options = selectOptions(el);
    if (el.maxLength > 0) field.max_length = el.maxLength;
    return field;
}

function kindOf(el) {
    if (el.type === "radio" || el.tagName === "SELECT") return "choice";
    if (el.type === "number") return "number";
    if (el.matches('[data-controller~="create-line-items"]')) return "items";
    return "text";
}

function labelOf(el, name) {
    if (el.dataset.chipFillLabel) return el.dataset.chipFillLabel;
    const label = el.id && document.querySelector(`label[for="${el.id}"]`);
    return tidy(label?.textContent) || el.getAttribute("placeholder") || name;
}

function radioOption(radio) {
    const label = radio.id && document.querySelector(`label[for="${radio.id}"]`);
    return { value: radio.value, label: tidy(label?.textContent) || radio.value };
}

function selectOptions(select) {
    return [...select.options]
        .filter((option) => option.value && !option.disabled)
        .map((option) => ({ value: option.value, label: tidy(option.textContent) }));
}

function tidy(text) {
    return (text || "").replace(/\s+/g, " ").replace(/\s*\*$/, "").trim();
}

// --- Writing a field ------------------------------------------------------
// Each writer returns a function that puts the field back, or null when it
// changed nothing. Text and radios are written by _field_write.js.

// Chip's pick fills the address's hidden parts (street, suburb, postcode,
// position, all inside the autocomplete container) the way a tap on the
// suggestion does. Undo puts back the text and every one of those parts.
function pickAddress(input, suggestion) {
    const autocomplete = input.autocompleteInstance;
    const container = input.closest('[data-controller~="autocomplete"]') || input.parentElement;
    const parts = [...container.querySelectorAll('input[type="hidden"]')];
    const text = input.value;
    const values = parts.map((part) => part.value);
    autocomplete.select(suggestion);
    flash(input);
    return () => {
        autocomplete.setText(text);
        parts.forEach((part, i) => { part.value = values[i]; });
    };
}

function chooseOption(select, value) {
    const before = select.value;
    if (before === value) return null;
    setSelect(select, value);
    return () => setSelect(select, before);
}

// Choices.js (select_controller.js) hides the real <select>; its instance is
// kept on the element. setChoiceByValue does not fire `change`, and the site
// and contact pickers listen for exactly that, so it is dispatched here.
function setSelect(select, value) {
    const choices = select.choicesInstance;
    if (choices && value) choices.setChoiceByValue(value);
    else if (choices) choices.removeActiveItems();
    select.value = value;
    select.dispatchEvent(new Event("change", { bubbles: true }));
}

function chooseRadio(radios, value) {
    const before = radios.find((radio) => radio.checked);
    const chosen = radios.find((radio) => radio.value === value);
    if (!chosen || chosen === before) return null;
    check(chosen);
    return () => (before ? check(before) : uncheck(chosen));
}

// A field in a collapsed create section (Notes) is opened the way the user
// would open it: by its toggle, the section's previous sibling.
function reveal(el) {
    const section = el.closest(".create-section-content");
    if (section && section.style.display === "none") section.previousElementSibling?.click();
}

function flash(el) {
    const target = el.type === "radio" ? document.querySelector(`label[for="${el.id}"]`)
        : el.closest(".choices") || el;
    if (!target) return;
    target.classList.add("chip-filled");
    setTimeout(() => target.classList.remove("chip-filled"), FLASH_MS);
}
